import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ParkingaboProductListItem } from '../../../shared/ParkingaboProductModels';
import { useLoggedParkingaboServerWrite } from '../../../api/ParkingaboApi';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { FeedbackPopup } from '../../../components/FeedbackPopup';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage';
import { Localized } from 'dg-web-shared/lib/Localization';
import { ProductDetailBody, ProductOverviewList } from './ProductsDetailOutlet';
import {
    MenuListItemColorVariant,
    ParkingaboListItem,
    ParkingaboListItemLabel,
} from '../../../components/ParkingaboMenuListItem.tsx';
import { Delete } from '@mui/icons-material';
import {
    contractReceiptUrl,
    ReceiptButton,
} from '../account-statement/AccountStatementOutlet.tsx';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';
import { ParkingaboProductType } from 'product-shared/product-template/ParkingaboProductType.ts';

interface ReservationCancellationPayload {
    contractExternalId: string;
}

export function ProductDetailReservation({
    product,
    refetchProducts,
}: {
    product: ParkingaboProductListItem;
    refetchProducts: () => void;
}) {
    const navigate = useNavigate();
    const { user } = useCustomerContext();

    const [cancelConfirmDialogOpen, setCancelConfirmDialogOpen] =
        useState(false);
    const [cancelSuccessDialogOpen, setCancelSuccessDialogOpen] =
        useState(false);

    const [cancellationState, cancelReservation] =
        useLoggedParkingaboServerWrite<ReservationCancellationPayload, never>(
            'storno-product',
            ({ contractExternalId }) => ({
                url: `user/self/product/${contractExternalId}`,
                method: RequestMethod.DELETE,
            }),
        );

    useServerSuccessEffect(cancellationState, () => {
        setCancelSuccessDialogOpen(true);
    });

    const validFrom = DateTime.fromISO(product.validFrom);
    const now = DateTime.now();
    const showCancelButton =
        now < validFrom && product.type === ParkingaboProductType.RESERVATION;

    if (product.type !== ParkingaboProductType.RESERVATION) {
        throw new Error('product type must be reservation');
    }

    return (
        <ProductDetailBody>
            <BackendRequestErrorMessage requestState={cancellationState} />
            <ProductOverviewList product={product} />
            {showCancelButton && (
                <ParkingaboListItem
                    onClick={() => setCancelConfirmDialogOpen(true)}
                    colorVariant={MenuListItemColorVariant.PRIMARY}
                >
                    <ParkingaboListItemLabel
                        loading={
                            cancellationState.status === RequestStatus.PENDING
                        }
                        label={
                            <Localized
                                de="Reservierung löschen"
                                fr="Supprimer la réservation"
                                it="Cancella riservazione"
                                en="Delete reservation"
                            />
                        }
                        icon={
                            <Delete
                                style={{ fontSize: 36 }}
                                color={'primary'}
                            />
                        }
                    />
                </ParkingaboListItem>
            )}
            {product.hasBooking && (
                <ReceiptButton
                    url={contractReceiptUrl(
                        user.operatorExternalId,
                        product.contractExternalId,
                        product.token,
                    )}
                />
            )}

            <CancellationConfirmDialog
                open={cancelConfirmDialogOpen}
                onAbort={() => setCancelConfirmDialogOpen(false)}
                onConfirm={() => {
                    cancelReservation({
                        contractExternalId: product.contractExternalId,
                    });
                    setCancelConfirmDialogOpen(false);
                }}
            />
            <CancelSuccessDialog
                open={cancelSuccessDialogOpen}
                onClick={() => {
                    refetchProducts();
                    setCancelSuccessDialogOpen(false);
                    navigate('..');
                }}
            />
        </ProductDetailBody>
    );
}

function CancellationConfirmDialog({
    open,
    onAbort,
    onConfirm,
}: {
    open: boolean;
    onAbort: () => void;
    onConfirm: () => void;
}) {
    return (
        <FeedbackPopup
            open={open}
            color="warning"
            title={
                <Localized
                    de="Reservierung löschen"
                    fr="Supprimer la réservation"
                    it="Cancella riservazione"
                    en="Delete reservation"
                />
            }
            onAbort={onAbort}
            onConfirm={onConfirm}
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
        >
            <Typography component="p" sx={{ paddingBottom: 1 }}>
                <Localized
                    de="Wenn Sie Ihre Reservierung jetzt löschen, "
                    fr="Si vous supprimez votre réservation maintenant, "
                    it="Cancellando adesso la riservazione "
                    en="If you delete your reservation now,  "
                />
                <b>
                    <Localized
                        de="werden keine Kosten"
                        fr="aucun coût ne sera débité"
                        it="non verrà addebitato alcuno costo"
                        en="no costs will be charged"
                    />
                </b>
                <Localized
                    de=" auf Ihrem Parkingabo Konto "
                    fr=" sur votre compte Parkingabo."
                    it=" sul suo conto Parkingabo."
                    en=" to your Parkingabo account."
                />
                <b>
                    <Localized de="belastet." fr="" it="" en="" />
                </b>
            </Typography>
            <Typography>
                <Localized
                    de="Möchten Sie diese Reservierung wirklich löschen?"
                    fr="Voulez-vous vraiment supprimer cette réservation?"
                    it="Vuole veramente cancellare questa riservazione?"
                    en="Do you really want to delete this reservation?"
                />
            </Typography>
        </FeedbackPopup>
    );
}

export function CancelSuccessDialog({
    open,
    onClick,
}: {
    open: boolean;
    onClick: () => void;
}) {
    return (
        <FeedbackPopup
            open={open}
            color="success"
            title={
                <Localized
                    de="Bestätigung"
                    fr="Confirmation"
                    it="Conferma"
                    en="Confirmation"
                />
            }
            abortLabel={'OK'}
            onAbort={onClick}
        >
            <Localized
                de="Die Reservierung wurde erfolgreich gelöscht."
                fr="La réservation a été supprimée avec succès."
                it="La riservazione è stata cancellata con successo."
                en="The reservation was successfully deleted."
            />
        </FeedbackPopup>
    );
}
